// hooks/useBillGenerator.ts
import { useState, useCallback, useRef } from 'react';

interface UseBillGeneratorReturn {
    generateBillForOrder: (orderId: number, orderType: string) => Promise<File>;
    isGenerating: boolean;
    billComponent: React.ReactNode | null;
    error: string | null;
    reset: () => void;
}

export const useBillGenerator = (): UseBillGeneratorReturn => {
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [billComponent, setBillComponent] = useState<React.ReactNode>(null);
    const [error, setError] = useState<string | null>(null);
    
    // Use ref to track the current state without dependency issues
    const generatingRef = useRef<boolean>(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const reset = useCallback(() => {
        setIsGenerating(false);
        setBillComponent(null);
        setError(null);
        generatingRef.current = false;
        
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const generateBillForOrder = useCallback(async (orderId: number, orderType: string): Promise<File> => {
        // Reset any previous state
        reset();
        
        setIsGenerating(true);
        setError(null);
        generatingRef.current = true;

        return new Promise(async (resolve, reject) => {
            try {
                // Dynamically import the SendBill component
                const SendBillModule = await import('../shared/SendBill');
                const SendBill = SendBillModule.default;

                // Handle bill generation success
                const handleBillGenerated = (file: File) => {
                    if (generatingRef.current) {
                        resolve(file);
                        reset();
                    }
                };

                // Handle errors
                const handleError = (errorMessage: string) => {
                    if (generatingRef.current) {
                        const error = new Error(errorMessage);
                        setError(errorMessage);
                        reject(error);
                        reset();
                    }
                };

                // Create a bill component that resolves the promise
                const BillComponent = (
                    <SendBill
                        orderId={orderId}
                        orderType={orderType}
                        onBillGenerated={handleBillGenerated}
                        onError={handleError}
                    />
                );

                setBillComponent(BillComponent);

                // Set timeout for bill generation (30 seconds)
                timeoutRef.current = setTimeout(() => {
                    if (generatingRef.current) {
                        const timeoutError = 'Bill generation timeout - took too long to generate';
                        setError(timeoutError);
                        reject(new Error(timeoutError));
                        reset();
                    }
                }, 30000);

            } catch (importError) {
                const errorMessage = `Failed to load bill component: ${importError instanceof Error ? importError.message : 'Unknown error'}`;
                setError(errorMessage);
                reject(new Error(errorMessage));
                reset();
            }
        });
    }, [reset]);

    return {
        generateBillForOrder,
        isGenerating,
        billComponent,
        error,
        reset
    };
};